/**
 * OptionBasketsMonitor — the "FNO → Baskets" monitor.
 *
 * Lists the option baskets placed from the Strategy Architect / chart builder
 * (PAPER or LIVE), with their status, legs, entry credit, auto TP/SL levels and
 * — for OPEN baskets — the LIVE net-to-close, unrealised P&L, per-leg marks,
 * a TP/SL progress gauge, time-to-expiry and the spot-vs-profit-zone, plus a
 * "Close now" control. Realised P&L appears once the monitor closes them.
 *
 * `compact` renders a tighter list (open baskets only) for embedding in the
 * Dashboard's Trade Management area. Polls every 15s.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CH_TYPE, chNumeric } from '../../styles/appUi';
import { useAppTheme } from '../../hooks/useAppTheme';
import {
  Box, Card, CardContent, Typography, Chip, ToggleButton, ToggleButtonGroup,
  CircularProgress, IconButton, Tooltip, Divider, Button, Collapse, InputBase,
  Dialog, DialogTitle, DialogContent, DialogActions, useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import toast from 'react-hot-toast';
import { usePersistedState } from '../../hooks/usePersistedState';
import { optionsService } from '../../services/options.service';
import BasketAdjustPanel from './BasketAdjustPanel';
import ClosedBasketPayoff from './ClosedBasketPayoff';
import { PlainStatus, Term } from './shared/OptionsUi';

type LiveState = {
  currentNet: number;
  /** NET ₹ (after exit slippage + both sides' fees, 2026-11-03). */
  unrealizedPnl: number;
  grossPnl?: number;
  costInr?: number;
  perLeg: Array<{ symbol: string; action: 'BUY' | 'SELL'; quantity: number; mark: number }>;
  spot: number | null;
  nearExpiryMs: number | null;
  markedAt: number;
};

type Basket = {
  id: number; strategy: string; status: string; tradingMode?: 'PAPER' | 'LIVE';
  legs: Array<{ action: 'BUY' | 'SELL'; optionType: 'CE' | 'PE'; strike: number; quantity: number; symbol: string; expiry?: string }>;
  marginEstimate?: number; maxProfit?: number; maxLoss?: number; netPremium?: number;
  entryCredit?: number | null; targetPremium?: number | null; slPremium?: number | null;
  tpPct?: number | null; slPct?: number | null;
  /** Fixed ₹ exits on TOTAL P&L (adjustment bookings + open legs) — Charts Options Workspace. */
  exitMode?: 'FIXED' | 'PREMIUM' | null; tpAmountInr?: number | null; slAmountInr?: number | null;
  ratioAlertX?: number | null; paperFill?: 'BOOK' | null;
  adjustmentsRealizedPnl?: number | null; adjustmentLog?: any[] | null;
  campaignId?: number | null;
  /** Opened by an option strategy attached to an NB/NS/BB/BS price level. */
  levelTriggerId?: number | null;
  /** Which exit closed it: TP_AMOUNT | TP_SPOT | SL_AMOUNT | SL_SPOT | TRAIL (exitReason stays TARGET/SL). */
  exitTrigger?: string | null;
  slUnderlyingLow?: number | null; slUnderlyingHigh?: number | null;
  tgtUnderlyingLow?: number | null; tgtUnderlyingHigh?: number | null;
  amountTrailEnabled?: boolean | null; trailLockInr?: number | null;
  monitorEnabled?: boolean; closedAt?: string | null; exitReason?: string | null; realizedPnl?: number | null;
  entrySpot?: number | null; breakEvens?: number[] | null;
  legFills?: Array<{ action?: string; symbol?: string; fillPrice?: number | null; quantity?: number; contractValue?: number }>;
  createdAt?: string;
  live?: LiveState;
  adjustmentNeeded?: boolean;
  /** Broker position-truth reconcile: legs no longer match the broker → auto
   *  TP/SL frozen until the user adopts broker truth or closes the basket. */
  desynced?: boolean;
  desyncInfo?: { at?: string; legs?: Array<{ symbol: string; expected: number; broker: number }>; resolution?: string } | null;
  adjustmentInfo?: { severity?: string; reasons?: string[]; testedSide?: string } | null;
};

const fmtInr = (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}`;
const fmtSigned = (v: number) => `${v >= 0 ? '+' : '−'}${fmtInr(Math.abs(v))}`;

/** Fixed ₹ plan on total P&L, when the basket has one. */
const fixedPlan = (b: Basket): { tp: number; sl: number } | null =>
  b.exitMode === 'FIXED' && Number(b.tpAmountInr) > 0 && Number(b.slAmountInr) !== 0 && Number.isFinite(Number(b.slAmountInr)) ? { tp: Number(b.tpAmountInr), sl: Number(b.slAmountInr) } : null;
/** ₹ already booked by adjustments (0 when none). */
const adjBooked = (b: Basket) => Number(b.adjustmentsRealizedPnl || 0);

const USD_TO_INR = 85;
/** Rupee value of a NATIVE (qty-weighted) premium figure for a basket — the leg
 *  premiums are quoted in the contract's native unit, so a credit like "18593.82"
 *  becomes ≈₹1,580 once scaled by the contract value × FX. This is what reconciles
 *  the (₹) realised P&L against the (native) entry credit. */
const premiumInr = (b: Basket, nativeAbs: number): number | null => {
  const cv = Number(b.legFills?.[0]?.contractValue) || null;
  return cv != null ? nativeAbs * cv * USD_TO_INR : null;
};
/** The ₹ profit/loss & ROI a basket would book if it exits via its take-profit or stop-loss.
 *  Mirrors the backend computeBasketTpSl: TP = tpPct% of MAX PROFIT, SL = slPct% of MAX LOSS
 *  (the defined risk) — with a credit floor of 2× the premium captured. Falls back to the premium
 *  base when max P/L isn't known. ROI is on the margin. Pass overrides to preview live edits. */
const basketExitMath = (b: Basket, tpPctV?: number, slPctV?: number) => {
  const e = b.entryCredit != null ? Number(b.entryCredit) : null;
  const cv = Number(b.legFills?.[0]?.contractValue) || null;
  if (e == null || cv == null) return null;
  const absE = Math.abs(e);
  const tpPct = tpPctV != null ? tpPctV : (b.tpPct != null ? Number(b.tpPct) : 50);
  const slPct = slPctV != null ? slPctV : (b.slPct != null ? Number(b.slPct) : 50);
  const premiumInrVal = absE * cv * USD_TO_INR;   // rupee value of the premium captured
  const maxPInr = b.maxProfit != null ? Math.abs(Number(b.maxProfit)) : null;
  const maxLInr = b.maxLoss != null ? Math.abs(Number(b.maxLoss)) : null;
  const tpInr = (tpPct / 100) * (maxPInr && maxPInr > 0 ? maxPInr : premiumInrVal);
  let slInr = (slPct / 100) * (maxLInr && maxLInr > 0 ? maxLInr : premiumInrVal);
  if (e > 0) slInr = Math.max(slInr, 2 * premiumInrVal);   // credit floor: never tighter than 2× credit
  const margin = b.marginEstimate != null ? Number(b.marginEstimate) : null;
  const tpRoi = margin && margin > 0 ? (tpInr / margin) * 100 : null;
  const slRoi = margin && margin > 0 ? (slInr / margin) * 100 : null;
  return { baseNativeAbs: absE, baseInr: premiumInrVal, tpInr, slInr, tpRoi, slRoi, tpPct, slPct };
};
const ts = (s?: string | null) => (s ? new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

/** Time-to-go for the near (first-settling) leg. */
const dteFmt = (ms?: number | null): { label: string; expired: boolean } | null => {
  if (!ms) return null;
  const diff = ms - Date.now();
  if (diff <= 0) return { label: 'settling', expired: true };
  const h = diff / 3.6e6, d = Math.floor(h / 24), hh = Math.floor(h % 24);
  return { label: d > 0 ? `${d}d ${hh}h` : `${hh}h ${Math.floor((h % 1) * 60)}m`, expired: false };
};

/** open = placed & still being managed; closed = squared off; pending/rejected = not placed. */
const phaseOf = (b: Basket): 'open' | 'closed' | 'pending' | 'other' => {
  if (b.status === 'EXECUTED' && !b.closedAt) return 'open';
  if (b.status === 'EXECUTED' && b.closedAt) return 'closed';
  // Placed but no legs executed yet (awaiting approval / trigger) — payoff still viewable.
  if (b.status === 'PENDING' || b.status === 'APPROVED' || b.status === 'PENDING_APPROVAL') return 'pending';
  return 'other';
};

/** Holding period between two ISO timestamps, e.g. "1d 4h" / "3h 12m". */
const heldFor = (from?: string | null, to?: string | null): string | null => {
  if (!from || !to) return null;
  const ms = new Date(to).getTime() - new Date(from).getTime();
  if (!(ms > 0)) return null;
  const h = ms / 3.6e6, d = Math.floor(h / 24), hh = Math.floor(h % 24), mm = Math.floor((h % 1) * 60);
  return d > 0 ? `${d}d ${hh}h` : hh > 0 ? `${hh}h ${mm}m` : `${mm}m`;
};

/** Plain-English outcome for a CLOSED basket — explains WHY the monitor closed it
 *  and whether it was a win or a loss. The exitReason is one of
 *  TARGET / SL / EXPIRY / MANUAL (optionally suffixed _PARTIAL_REVIEW). */
const outcomeOf = (b: Basket): { title: string; detail: string; tone: 'good' | 'bad' | 'neutral'; emoji: string; partial: boolean } => {
  const raw = String(b.exitReason || '').toUpperCase();
  const partial = raw.includes('PARTIAL');
  const base = raw.replace('_PARTIAL_REVIEW', '');
  const pnl = b.realizedPnl != null ? Number(b.realizedPnl) : null;
  const win = pnl != null && pnl >= 0;
  const maxP = b.maxProfit != null ? Math.abs(Number(b.maxProfit)) : null;
  const maxL = b.maxLoss != null ? Math.abs(Number(b.maxLoss)) : null;
  let title = b.exitReason || 'Closed', detail = '', emoji = '✓';
  if (base === 'TARGET') {
    title = 'Profit target hit'; emoji = '🎯';
    // Rupee-based wording — the realised P&L is in ₹ while the entry "credit" is a
    // native premium figure, so framing the booking as a % of MAX PROFIT (also ₹)
    // is what actually reconciles (e.g. ₹798 ≈ 50% of a ₹1,580 max profit).
    const tp = b.tpPct != null ? Math.round(Number(b.tpPct)) : null;
    const pctOfMax = (pnl != null && maxP) ? Math.round((pnl / maxP) * 100) : null;
    const fp = fixedPlan(b);
    if (fp) detail = `Total P&L (adjustments + open legs) reached your fixed target of ${fmtSigned(fp.tp)}, so the monitor closed it and booked ${pnl != null ? fmtSigned(pnl) : 'the gain'} in total.`;
    else detail = `The basket reached your take-profit level${tp != null ? ` (book at ${tp}% of max profit)` : ''}, so the monitor closed it and booked ${pnl != null ? fmtSigned(pnl) : 'the gain'}`
      + `${pctOfMax != null && maxP != null ? ` — about ${pctOfMax}% of this basket's max profit (${fmtInr(maxP)}).` : '.'}`;
  } else if (base === 'SL') {
    title = 'Stop-loss hit'; emoji = '🛑';
    const sl = b.slPct != null ? Math.round(Number(b.slPct)) : null;
    const pctOfMaxL = (pnl != null && maxL) ? Math.round((Math.abs(pnl) / maxL) * 100) : null;
    const fp = fixedPlan(b);
    if (fp) detail = `Total P&L (adjustments + open legs) reached your fixed stop of ${fmtSigned(-fp.sl)}, so the monitor closed it — ${pnl != null ? fmtSigned(pnl) : 'the stop'} in total.`;
    else detail = `The loss reached your stop level${sl != null ? ` (stop at ${sl}% of max loss)` : ''}, so the monitor closed it to cap the loss at ${pnl != null ? fmtSigned(pnl) : 'the stop'}`
      + `${pctOfMaxL != null && maxL != null ? ` — about ${pctOfMaxL}% of max loss (${fmtInr(maxL)}).` : '.'}`;
  } else if (base === 'EXPIRY') {
    title = 'Rolled off before expiry'; emoji = '⏳';
    detail = 'A leg was about to settle, so the whole structure was squared off cleanly to avoid leaving a naked/expired leg.';
  } else if (base === 'MANUAL') {
    title = 'Closed manually'; emoji = '✋';
    detail = 'You squared this basket off from the app — the monitor stopped managing it and booked the realised P&L.';
  }
  // Which of the fixed exits fired (level-attached / workspace baskets): the price
  // targets and the profit lock read differently from the ₹ amounts.
  const trig = String(b.exitTrigger || '').toUpperCase();
  const lvl = (lo?: number | null, hi?: number | null) => (lo != null ? Number(lo) : hi != null ? Number(hi) : null);
  if (trig === 'TP_SPOT') {
    title = 'Price target hit'; emoji = '🎯';
    const p = lvl(b.tgtUnderlyingLow, b.tgtUnderlyingHigh);
    detail = `The underlying reached your target price${p != null ? ` (${p.toLocaleString('en-IN')})` : ''} before the ₹ target, so the monitor closed it and booked ${pnl != null ? fmtSigned(pnl) : 'the result'}.`;
  } else if (trig === 'SL_SPOT') {
    title = 'Price stop hit'; emoji = '🛑';
    const p = lvl(b.slUnderlyingLow, b.slUnderlyingHigh);
    detail = `The underlying crossed your stop price${p != null ? ` (${p.toLocaleString('en-IN')})` : ''} before the ₹ stop, so the monitor closed it — ${pnl != null ? fmtSigned(pnl) : 'the stop'} in total.`;
  } else if (trig === 'TRAIL') {
    title = 'Profit lock hit'; emoji = '🔒';
    detail = `Auto-Trail had locked ${b.trailLockInr != null ? fmtSigned(Number(b.trailLockInr)) : 'part of the peak profit'}; P&L fell back to that lock, so the monitor closed it and kept ${pnl != null ? fmtSigned(pnl) : 'the gain'}.`;
  }
  if (partial) detail += ' ⚠️ One or more legs did not confirm flat — verify the position at Delta.';
  return { title, detail, tone: partial ? 'neutral' : win ? 'good' : 'bad', emoji, partial };
};

/** Plain-English "how is this OPEN basket doing right now" — the one line a beginner
 *  reads instead of decoding the gauge/numbers. Frames profit as progress toward the
 *  take-profit and loss as progress toward the stop, plus whether spot is still inside
 *  the profit zone, and surfaces an adjustment flag first. */
const liveBasketStatus = (b: Basket): { tone: 'good' | 'bad' | 'warn' | 'neutral'; emoji: string; text: string } | null => {
  const live = b.live;
  if (!live) return null;
  // Zone check (for range/credit structures with short strikes).
  const shortPut = b.legs?.filter(l => l.action === 'SELL' && l.optionType === 'PE').map(l => l.strike).sort((a, c) => a - c)[0];
  const shortCall = b.legs?.filter(l => l.action === 'SELL' && l.optionType === 'CE').map(l => l.strike).sort((a, c) => c - a)[0];
  const hasZone = shortPut != null || shortCall != null;
  const spot = live.spot;
  const inZone = spot != null && (shortPut == null || spot >= shortPut) && (shortCall == null || spot <= shortCall);
  const fpl = fixedPlan(b);
  // Fixed-₹ baskets are judged on TOTAL P&L (what adjustments booked + the open legs).
  const pnl = fpl || adjBooked(b) ? live.unrealizedPnl + adjBooked(b) : live.unrealizedPnl;

  if (b.adjustmentNeeded) {
    const urgent = b.adjustmentInfo?.severity === 'urgent';
    return {
      tone: urgent ? 'bad' : 'warn', emoji: urgent ? '🚨' : '⚠️',
      text: urgent
        ? 'Needs attention now — the price has moved against the plan. Review or adjust below.'
        : 'Keep an eye on this — the price is testing the edge of your plan.',
    };
  }

  // Progress along the entry → TP (profit) / entry → SL (loss) axis using net-to-close.
  const entry = b.entryCredit != null ? Number(b.entryCredit) : null;
  const tgt = b.targetPremium != null ? Number(b.targetPremium) : null;
  const sl = b.slPremium != null ? Number(b.slPremium) : null;
  const now = live.currentNet;
  let progressTxt = '';
  if (fpl && fpl.sl < 0) {
    // Profit-floor stop: say how much room is left above the floor.
    progressTxt = ` — ${fmtSigned(fpl.tp - pnl)} to your ${fmtSigned(fpl.tp)} target; the stop locks ${fmtSigned(-fpl.sl)}`;
  } else if (fpl) {
    progressTxt = pnl >= 0
      ? ` — about ${Math.max(0, Math.min(100, Math.round((pnl / fpl.tp) * 100)))}% of the way to your ${fmtSigned(fpl.tp)} target`
      : ` — about ${Math.max(0, Math.min(100, Math.round((-pnl / fpl.sl) * 100)))}% of the way to your ${fmtSigned(-fpl.sl)} stop`;
  } else if (entry != null && tgt != null && sl != null && now != null) {
    const lo = Math.min(tgt, sl), hi = Math.max(tgt, sl), span = hi - lo || 1;
    const pos = (v: number) => 100 - ((v - lo) / span) * 100;       // 100 = at TP, 0 = at SL
    const pNow = pos(now), pEntry = pos(entry);
    if (pNow >= pEntry) {
      const toTp = pEntry < 100 ? Math.round(((pNow - pEntry) / (100 - pEntry)) * 100) : 0;
      progressTxt = ` — about ${Math.max(0, Math.min(100, toTp))}% of the way to your take-profit`;
    } else {
      const toSl = pEntry > 0 ? Math.round(((pEntry - pNow) / pEntry) * 100) : 0;
      progressTxt = ` — about ${Math.max(0, Math.min(100, toSl))}% of the way to your stop-loss`;
    }
  }
  const zoneTxt = hasZone && spot != null ? (inZone ? ' Price is inside your profit zone.' : ' Price has drifted outside your profit zone — watch it.') : '';

  if (pnl == null) return { tone: 'neutral', emoji: '⏳', text: `Waiting for a live price.${zoneTxt}` };
  if (pnl >= 0) return { tone: 'good', emoji: '📈', text: `On track — up ${fmtSigned(pnl)}${fpl || adjBooked(b) ? ' in total' : ''} right now${progressTxt}.${zoneTxt}` };
  return { tone: (hasZone && !inZone) ? 'warn' : 'neutral', emoji: '📉', text: `Down ${fmtSigned(pnl)}${fpl || adjBooked(b) ? ' in total' : ''} right now${progressTxt}.${zoneTxt}` };
};

/** Underlying symbol a basket belongs to — Delta legs are {C|P}-BASE-STRIKE-DDMMYY;
 *  NSE legs start with the underlying name then digits. Used to group baskets. */
const underlyingOf = (b: Basket): string => {
  const sym = String(b.legFills?.[0]?.symbol || b.legs?.[0]?.symbol || '').toUpperCase();
  const delta = /^[CP]-([A-Z0-9]+)-/.exec(sym);
  if (delta) return delta[1];
  const nse = /^([A-Z&]+?)\d/.exec(sym);
  if (nse) return nse[1];
  return sym || '—';
};

/** Group baskets by underlying, preserving first-seen order, sorted A→Z. */
const groupByUnderlying = (list: Basket[]): Array<[string, Basket[]]> => {
  const m = new Map<string, Basket[]>();
  for (const b of list) { const u = underlyingOf(b); if (!m.has(u)) m.set(u, []); m.get(u)!.push(b); }
  return Array.from(m.entries()).sort((a, c) => a[0].localeCompare(c[0]));
};

/** Split a symbol's baskets into staged-campaign clusters (same campaignId) + standalone baskets,
 *  so a campaign reads as ONE trade with its tranches (E1/E2/E3…) as order-groups underneath. */
const clusterByCampaign = (list: Basket[]): Array<{ campaignId: number | null; items: Basket[] }> => {
  const camps = new Map<number, Basket[]>();
  const solo: Basket[] = [];
  for (const b of list) {
    if (b.campaignId != null) { if (!camps.has(b.campaignId)) camps.set(b.campaignId, []); camps.get(b.campaignId)!.push(b); }
    else solo.push(b);
  }
  const out: Array<{ campaignId: number | null; items: Basket[] }> = [];
  Array.from(camps.entries()).sort((a, c) => a[0] - c[0]).forEach(([cid, items]) => out.push({ campaignId: cid, items: items.slice().sort((a, b) => a.id - b.id) }));
  if (solo.length) out.push({ campaignId: null, items: solo });
  return out;
};

/** Consolidated P&L for a set of tranches: realised (closed) + live unrealised (open) — all
 *  broker-sourced, so the campaign total matches Delta. */
const clusterPnl = (items: Basket[]) => {
  let realized = 0, unreal = 0, hasLive = false, anyClosed = false, anyOpen = false;
  for (const b of items) {
    const p = phaseOf(b);
    if (p === 'closed') { realized += Number(b.realizedPnl || 0); anyClosed = true; }
    else if (p === 'open') { anyOpen = true; if (b.live) { unreal += Number(b.live.unrealizedPnl || 0); hasLive = true; } }
  }
  return { realized, unreal, total: realized + unreal, hasLive, anyClosed, anyOpen };
};

/** A stable, distinct accent per underlying symbol — a curated set of pleasant hues
 *  picked by a hash of the symbol, rendered theme-aware (tinted bg + left bar + text)
 *  so each group header reads clearly in both light and dark. */
const SYMBOL_HUES = [210, 145, 280, 33, 340, 190, 95, 255, 12, 165, 50, 305];
const symbolAccent = (sym: string, isDark: boolean) => {
  let h = 0; for (let i = 0; i < sym.length; i++) h = (h * 31 + sym.charCodeAt(i)) >>> 0;
  const hue = SYMBOL_HUES[h % SYMBOL_HUES.length];
  return {
    // Lightness pushed apart from the background: 72/36 measured 3.15-3.91:1 once the header
    // chip sits inside another tinted container, which it does on the Dashboard. 82/24 clears
    // 4.6:1 even when the tint is effectively applied twice. The hue still carries the
    // per-symbol identity; only the lightness moved.
    text: `hsl(${hue}, 75%, ${isDark ? 82 : 24}%)`,
    border: `hsl(${hue}, 70%, ${isDark ? 55 : 48}%)`,
    // 0.12 in dark, down from 0.16. The group header carries text.secondary on top of this
    // tint, and at 0.16 the two compounded to 3.06-4.16:1. Lowering the tint keeps the
    // per-symbol identity the colour exists for while giving every label on it back its
    // contrast — cheaper than brightening every colour that might sit here.
    bg: `hsla(${hue}, ${isDark ? 70 : 75}%, ${isDark ? 55 : 50}%, ${isDark ? 0.12 : 0.10})`,
    bgHover: `hsla(${hue}, ${isDark ? 70 : 75}%, ${isDark ? 55 : 50}%, ${isDark ? 0.26 : 0.18})`,
  };
};

/** Collapsible header for a per-underlying basket group: symbol + count + paper/live
 *  mix + aggregate live P&L. Each symbol gets its own accent colour. */
const GroupHeader: React.FC<{ sym: string; list: Basket[]; open: boolean; onToggle: () => void; dense?: boolean }> = ({ sym, list, open, onToggle, dense }) => {
  const { C } = useAppTheme();
  const hasLive = list.some(b => b.live);
  const liveSum = list.reduce((s, b) => s + (b.live?.unrealizedPnl || 0), 0);
  const paper = list.filter(b => b.tradingMode === 'PAPER').length;
  const live = list.length - paper;
  const isDark = useTheme().palette.mode === 'dark';
  const ac = symbolAccent(sym, isDark);
  return (
    <Box onClick={onToggle} sx={{
      display: 'flex', alignItems: 'center', gap: 0.7, cursor: 'pointer', userSelect: 'none',
      py: dense ? 0.4 : 0.6, px: 1, borderRadius: 1, bgcolor: ac.bg, borderLeft: `3px solid ${ac.border}`,
      transition: 'background-color .15s', '&:hover': { bgcolor: ac.bgHover },
    }}>
      {open ? <KeyboardArrowDownIcon fontSize="small" sx={{ color: ac.text }} /> : <KeyboardArrowRightIcon fontSize="small" sx={{ color: ac.text }} />}
      <Typography sx={{ fontWeight: 800, fontSize: dense ? '0.82rem' : '0.95rem', color: ac.text }}>{sym}</Typography>
      <Chip size="small" label={`${list.length} basket${list.length === 1 ? '' : 's'}`} sx={{ height: 18, fontSize: '0.58rem', fontWeight: 800 }} />
      {paper > 0 && <Chip size="small" label={`${paper}P`} color="warning" sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700 }} />}
      {live > 0 && <Chip size="small" label={`${live}L`} color="error" sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700 }} />}
      {hasLive && (
        <Chip size="small" label={`P&L ${fmtSigned(liveSum)}`} sx={{ height: 18, fontSize: '0.62rem', fontWeight: 800, ml: 'auto', bgcolor: 'transparent', border: `1px solid ${liveSum >= 0 ? C.bullBorder : C.bearBorder}`, color: liveSum >= 0 ? C.bull : C.bear }} />
      )}
    </Box>
  );
};

/** A staged campaign shown as ONE consolidated trade: a purple-accented collapsible card with the
 *  campaign's combined broker-sourced P&L, and each fired tranche (E1/E2/E3…) as an order-group
 *  (BasketRow) underneath. Answers "did I make money?" at the campaign level. */
const CampaignCluster: React.FC<{ campaignId: number; items: Basket[]; onClose: (b: Basket) => void; onRefresh: () => void }> = ({ campaignId, items, onClose, onRefresh }) => {
  const { C } = useAppTheme();
  const [open, setOpen] = usePersistedState<boolean>(`optionBaskets.camp.${campaignId}.open.v1`, true);
  const pnl = clusterPnl(items);
  const isPaper = items[0]?.tradingMode === 'PAPER';
  const nOpen = items.filter(b => phaseOf(b) === 'open').length;
  const nClosed = items.filter(b => phaseOf(b) === 'closed').length;
  const tint = pnl.total >= 0 ? C.bull : C.bear;
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderLeft: `4px solid ${C.accent}`, borderRadius: 1, p: 0.75, bgcolor: C.accentMuted }}>
      <Box onClick={() => setOpen(o => !o)} sx={{ display: 'flex', alignItems: 'center', gap: 0.7, cursor: 'pointer', userSelect: 'none', flexWrap: 'wrap' }}>
        {open ? <KeyboardArrowDownIcon fontSize="small" sx={{ color: C.accent }} /> : <KeyboardArrowRightIcon fontSize="small" sx={{ color: C.accent }} />}
        <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: C.accent }}>🧩 Staged campaign #{campaignId}</Typography>
        <Chip size="small" label={isPaper ? 'PAPER' : 'LIVE'} color={isPaper ? 'warning' : 'error'} sx={{ height: 16, fontSize: '0.52rem', fontWeight: 800 }} />
        <Chip size="small" label={`${items.length} tranche${items.length === 1 ? '' : 's'}`} sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700 }} />
        {nOpen > 0 && <Chip size="small" label={`${nOpen} open`} color="info" sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700 }} />}
        {nClosed > 0 && <Chip size="small" label={`${nClosed} closed`} sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700 }} />}
        <Tooltip arrow title="Consolidated P&L across every tranche in this campaign — realised on closed legs + live unrealised on open ones. Broker-sourced, so it matches Delta.">
          <Chip size="small" label={`P&L ${(pnl.hasLive || pnl.anyClosed) ? fmtSigned(pnl.total) : '—'}`} sx={{ height: 19, fontSize: '0.66rem', fontWeight: 800, ml: 'auto', bgcolor: 'transparent', border: `1px solid ${tint}59`, color: tint }} />
        </Tooltip>
      </Box>
      {open && pnl.anyClosed && pnl.anyOpen && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', pl: 3.4 }}>
          realised {fmtSigned(pnl.realized)} · open {pnl.hasLive ? fmtSigned(pnl.unreal) : 'marking…'}
        </Typography>
      )}
      <Collapse in={open} unmountOnExit>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1, mt: 1 }}>
          {items.map(b => <BasketRow key={b.id} b={b} onClose={() => onClose(b)} onRefresh={onRefresh} />)}
        </Box>
      </Collapse>
    </Box>
  );
};

const OptionBasketsMonitor: React.FC<{ compact?: boolean; liveOnly?: boolean }> = ({ compact = false, liveOnly = false }) => {
  const { C } = useAppTheme();
  const [allBaskets, setAllBaskets] = useState<Basket[]>([]);
  // On the Dashboard (`liveOnly`) the real-money total counts LIVE baskets only. Open PAPER
  // baskets are listed too (marked PAPER, e.g. a ⚡ level strategy under test) unless the
  // user hides them — persisted. Paper/Real filter (Trades + Options) — persisted.
  const [modeFilter, setModeFilter] = usePersistedState<'all' | 'live' | 'paper'>('optionBaskets.modeFilter.v1', 'all');
  const [dashPaper, setDashPaper] = usePersistedState<boolean>('optionBaskets.dashPaper.v1', true);
  const baskets = useMemo(() => {
    let list = liveOnly ? allBaskets.filter(b => b.tradingMode !== 'PAPER' || dashPaper) : allBaskets;
    if (!liveOnly && modeFilter !== 'all') list = list.filter(b => modeFilter === 'paper' ? b.tradingMode === 'PAPER' : b.tradingMode !== 'PAPER');
    return list;
  }, [allBaskets, liveOnly, modeFilter, dashPaper]);
  const modeCounts = useMemo(() => {
    let live = 0, paper = 0;
    (liveOnly ? [] : allBaskets).forEach(b => { if (b.tradingMode === 'PAPER') paper++; else live++; });
    return { live, paper, all: live + paper };
  }, [allBaskets, liveOnly]);
  const [loading, setLoading] = useState(false);
  // Persisted so the filter survives navigating away and back (was plain useState).
  /* Default to LIVE (open + pending) rather than All, so the page opens on what
     is actionable instead of on months of closed history. `live` falls back to
     showing everything when there is nothing open or pending — landing on an
     EMPTY section would be worse than landing on history, and right now open and
     pending are both zero. The key is unchanged, so an explicit choice you have
     already made still wins. */
  const [filter, setFilter] = usePersistedState<'all' | 'live' | 'open' | 'pending' | 'closed'>('optionBaskets.filter.v1', 'live');
  // Per-underlying group collapse state (default open) — persisted across navigation.
  const [groupOpen, setGroupOpen] = usePersistedState<Record<string, boolean>>(`optionBaskets.groupOpen.${compact ? 'c' : 'f'}.v1`, {});
  const toggleGroup = useCallback((sym: string) => setGroupOpen(prev => ({ ...prev, [sym]: !(prev[sym] ?? true) })), [setGroupOpen]);
  const [closeTarget, setCloseTarget] = useState<Basket | null>(null);
  const [closing, setClosing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r: any = await optionsService.getBaskets(undefined as any);
      const rows: Basket[] = (r?.data?.data || r?.data || []) as Basket[];
      setAllBaskets(Array.isArray(rows) ? rows : []);
    } catch { /* keep last */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const id = setInterval(load, 15_000); return () => clearInterval(id); }, [load]);

  const doClose = useCallback(async () => {
    if (!closeTarget) return;
    setClosing(true);
    try {
      const r: any = await optionsService.closeBasket(closeTarget.id);
      const pnl = r?.data?.data?.realizedPnl;
      toast.success(`Basket #${closeTarget.id} closed${typeof pnl === 'number' ? ` — P&L ${fmtSigned(pnl)}` : ''}.`, { duration: 7000 });
      setCloseTarget(null);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.message || 'Close failed', { duration: 8000 });
    } finally { setClosing(false); }
  }, [closeTarget, load]);

  const rows = useMemo(() => {
    const live = baskets.filter(b => { const p = phaseOf(b); return p === 'open' || p === 'pending'; });
    /* 'live' means open + pending, but falls back to everything when there is
       nothing live — an empty section reads as "broken", not as "all clear". */
    if (filter === 'live') return live.length ? live : baskets;
    return baskets.filter(b => (filter === 'all' ? true : phaseOf(b) === filter));
  }, [baskets, filter]);
  const counts = useMemo(() => {
    let open = 0, closed = 0, pending = 0;
    baskets.forEach(b => { const p = phaseOf(b); if (p === 'open') open++; else if (p === 'closed') closed++; else if (p === 'pending') pending++; });
    return { open, closed, pending, all: baskets.length };
  }, [baskets]);

  // ── Compact (dashboard) — open baskets only, terse ─────────────────────────
  if (compact) {
    const open = baskets.filter(b => phaseOf(b) === 'open');
    const openPaperAll = allBaskets.filter(b => b.tradingMode === 'PAPER' && phaseOf(b) === 'open').length;
    if (!open.length && !openPaperAll && !loading) return null; // nothing to show — keep the dashboard clean
    const realOpen = open.filter(b => b.tradingMode !== 'PAPER');
    const paperOpen = open.filter(b => b.tradingMode === 'PAPER');
    const totalLive = realOpen.reduce((s, b) => s + (b.live?.unrealizedPnl || 0), 0);
    const totalPaper = paperOpen.reduce((s, b) => s + (b.live?.unrealizedPnl || 0), 0);
    return (
      // A blended SUB-SECTION of the Trades Management panel (not a separate Card):
      // a subtle tinted block with a left accent, so option baskets clearly read as
      // part of Trades Management. Returns null above when there are no open baskets.
      <Box sx={{ mb: 2, p: 1.25, borderRadius: 1.5, borderLeft: `3px solid ${C.neutral}`, bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(30,136,229,0.08)' : 'rgba(30,136,229,0.05)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>Option baskets</Typography>
          {realOpen.length > 0 && <Chip size="small" label={`${realOpen.length} live open`} color="info" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />}
          {openPaperAll > 0 && (
            <Tooltip arrow title={dashPaper ? 'Hide open PAPER baskets from the Dashboard (they stay on Options → Monitor and Trades)' : 'Show open PAPER baskets here'}>
              <Chip size="small" data-testid="dash-paper-toggle" onClick={() => setDashPaper(v => !v)}
                label={dashPaper ? `${openPaperAll} paper · hide` : `${openPaperAll} paper hidden · show`}
                color="warning" variant={dashPaper ? 'filled' : 'outlined'} sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer' }} />
            </Tooltip>
          )}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {paperOpen.some(b => b.live) && (
              <Chip size="small" label={`Paper P&L ${fmtSigned(totalPaper)}`} variant="outlined" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, color: 'text.secondary' }} />
            )}
            {realOpen.some(b => b.live) && (
              <Chip size="small" label={`Live P&L ${fmtSigned(totalLive)}`} sx={{ height: 18, fontSize: '0.62rem', fontWeight: 800, bgcolor: 'transparent', border: `1px solid ${totalLive >= 0 ? C.bullBorder : C.bearBorder}`, color: totalLive >= 0 ? C.bull : C.bear }} />
            )}
            {loading && <CircularProgress size={13} />}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {groupByUnderlying(open).map(([sym, list]) => {
            const isOpen = groupOpen[sym] ?? true;
            return (
              <Box key={sym}>
                <GroupHeader sym={sym} list={list} open={isOpen} onToggle={() => toggleGroup(sym)} dense />
                <Collapse in={isOpen} unmountOnExit>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                    {list.map(b => <CompactRow key={b.id} b={b} onClose={() => setCloseTarget(b)} onRefresh={load} />)}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
        <CloseDialog target={closeTarget} closing={closing} onCancel={() => setCloseTarget(null)} onConfirm={doClose} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 800 }}>Placed baskets</Typography>
        <Tooltip arrow title="Baskets you placed from the Strategy Architect or the chart builder — PAPER (simulated) or LIVE. They're auto-managed by the basket monitor: it books profit at your take-profit level, caps losses at your stop-loss (both adjustable per basket), and rolls the structure off cleanly before expiry. Open baskets show the LIVE net-to-close + unrealised P&L; you can also Close now.">
          <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)} sx={{ ml: 1 }}>
          <ToggleButton value="live" sx={{ textTransform: 'none', px: 1.25 }}>Live ({counts.open + counts.pending})</ToggleButton>
          <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.25 }}>All ({counts.all})</ToggleButton>
          <ToggleButton value="open" sx={{ textTransform: 'none', px: 1.25 }}>Open ({counts.open})</ToggleButton>
          <ToggleButton value="pending" sx={{ textTransform: 'none', px: 1.25 }}>Pending ({counts.pending})</ToggleButton>
          <ToggleButton value="closed" sx={{ textTransform: 'none', px: 1.25 }}>Closed ({counts.closed})</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={modeFilter} onChange={(_, v) => v && setModeFilter(v)}>
          <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.25 }}>Both ({modeCounts.all})</ToggleButton>
          <ToggleButton value="live" sx={{ textTransform: 'none', px: 1.25, '&.Mui-selected': { color: C.bear } }}>Real ({modeCounts.live})</ToggleButton>
          <ToggleButton value="paper" sx={{ textTransform: 'none', px: 1.25, '&.Mui-selected': { color: C.warn } }}>Paper ({modeCounts.paper})</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {loading && <CircularProgress size={15} />}
          <Tooltip arrow title="Refresh"><IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
        </Box>
      </Box>

      {rows.length === 0 ? (
        <Card variant="outlined"><CardContent>
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading…' : 'No baskets yet. Place one from “Build from outlook” (📝 Paper trade or 🟢 Place LIVE) or from the chart builder, and it will appear here.'}
          </Typography>
        </CardContent></Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {groupByUnderlying(rows).map(([sym, list]) => {
            const isOpen = groupOpen[sym] ?? true;
            return (
              <Box key={sym}>
                <GroupHeader sym={sym} list={list} open={isOpen} onToggle={() => toggleGroup(sym)} />
                <Collapse in={isOpen} unmountOnExit>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                    {clusterByCampaign(list).map(cl => cl.campaignId != null ? (
                      <CampaignCluster key={`camp-${cl.campaignId}`} campaignId={cl.campaignId} items={cl.items} onClose={setCloseTarget} onRefresh={load} />
                    ) : (
                      <Box key="solo" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                        {cl.items.map(b => <BasketRow key={b.id} b={b} onClose={() => setCloseTarget(b)} onRefresh={load} />)}
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}
      <CloseDialog target={closeTarget} closing={closing} onCancel={() => setCloseTarget(null)} onConfirm={doClose} />
    </Box>
  );
};

/** TP/SL gauge — maps the live net-to-close between target (left, profit) and SL
 *  (right, loss); marks entry and the live position. */
const TpSlGauge: React.FC<{ entry: number; target: number; sl: number; now: number }> = ({ entry, target, sl, now }) => {
  const { C } = useAppTheme();
  const lo = Math.min(target, sl), hi = Math.max(target, sl);
  const span = hi - lo || 1;
  const pct = (v: number) => Math.max(0, Math.min(100, ((v - lo) / span) * 100));
  // currentNet shrinking toward target = profit, so invert for an intuitive
  // left=profit/right=loss bar (target on the left).
  const posNow = 100 - pct(now), posEntry = 100 - pct(entry);
  return (
    <Box sx={{ mt: 0.5 }}>
      <Box sx={{ position: 'relative', height: 8, borderRadius: 4, background: `linear-gradient(90deg,${C.bull},${C.bullMuted} 45%,${C.warnMuted} 55%,${C.bear})` }}>
        <Box sx={{ position: 'absolute', top: -2, bottom: -2, left: `calc(${posEntry}% - 1px)`, width: 2, bgcolor: 'rgba(0,0,0,0.45)' }} />
        <Box sx={{ position: 'absolute', top: -3, bottom: -3, left: `calc(${posNow}% - 4px)`, width: 8, height: 14, borderRadius: '50%', bgcolor: '#fff', border: '2px solid #333', boxShadow: 1 }} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3 }}>
        <Typography sx={{ fontSize: '0.54rem', color: C.bull, fontWeight: 700 }}>TP {target.toFixed(0)}</Typography>
        <Typography sx={{ fontSize: '0.54rem', color: 'text.secondary' }}>entry {entry.toFixed(0)}</Typography>
        <Typography sx={{ fontSize: '0.54rem', color: C.bear, fontWeight: 700 }}>SL {sl.toFixed(0)}</Typography>
      </Box>
    </Box>
  );
};

/** Fixed ₹ exits on TOTAL P&L: show the plan, edit the two amounts, or switch back to %. */
const FixedExitEditor: React.FC<{ b: Basket; onRefresh: () => void }> = ({ b, onRefresh }) => {
  const { C } = useAppTheme();
  const fp = fixedPlan(b)!;
  const [editing, setEditing] = useState(false);
  const [tp, setTp] = useState<number>(fp.tp);
  const [slAt, setSlAt] = useState<number>(-fp.sl);   // signed total-P&L stop level
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!editing) { setTp(fp.tp); setSlAt(-fp.sl); } }, [fp.tp, fp.sl, editing]);
  const save = async (body: any, msg: string) => {
    setSaving(true);
    try { await optionsService.updateBasketLevels(b.id, body); toast.success(msg, { duration: 5000 }); setEditing(false); onRefresh(); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || e?.message || 'Update failed', { duration: 7000 }); }
    finally { setSaving(false); }
  };
  const num = (v: number, set: (n: number) => void) => (
    <InputBase value={v} onChange={e => { const n = parseInt(e.target.value.replace(/[^0-9]/g, ''), 10); set(Number.isFinite(n) ? n : 0); }}
      onFocus={e => e.target.select()}
      inputProps={{ inputMode: 'numeric', style: { textAlign: 'right', padding: 0, fontSize: '0.72rem', fontWeight: 800 } }}
      sx={{ width: 70, px: 0.4, border: '1px solid', borderColor: 'divider', borderRadius: 0.5 }} />
  );
  return (
    <Box sx={{ mt: 0.6, ...(editing ? { p: 0.6, borderRadius: 1, bgcolor: 'action.hover' } : {}) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.64rem', color: 'text.secondary', fontWeight: 700 }}>Auto-close (fixed ₹, total P&amp;L)</Typography>
        {editing ? (
          <>
            <Typography sx={{ fontSize: '0.6rem', color: C.bull, fontWeight: 700 }}>TP +₹</Typography>{num(tp, setTp)}
            <Tooltip arrow title="Stop as total P&L: negative = a loss stop, positive = lock in a minimum profit (must stay below the target)">
              <Typography sx={{ fontSize: '0.6rem', color: C.bear, fontWeight: 700 }}>SL at ₹</Typography>
            </Tooltip>
            <InputBase value={slAt} onChange={e => { const t = e.target.value.replace(/[^0-9-]/g, ''); const n = parseInt(t, 10); setSlAt(t === '-' ? 0 : Number.isFinite(n) ? n : 0); }}
              onFocus={e => e.target.select()}
              inputProps={{ inputMode: 'text', style: { textAlign: 'right', padding: 0, fontSize: '0.72rem', fontWeight: 800 } }}
              sx={{ width: 76, px: 0.4, border: '1px solid', borderColor: slAt >= tp || slAt === 0 ? 'error.main' : 'divider', borderRadius: 0.5 }} />
            <Button size="small" variant="contained" disabled={saving || !(tp > 0) || slAt === 0 || slAt >= tp} sx={{ minWidth: 0, py: 0, px: 1, fontSize: '0.6rem' }}
              onClick={() => save({ exitMode: 'FIXED', tpAmountInr: tp, slAmountInr: -slAt }, `Basket #${b.id}: fixed exits TP +₹${tp} / SL ${fmtSigned(slAt)}`)}>Save</Button>
            <Button size="small" variant="text" sx={{ minWidth: 0, py: 0, px: 0.6, fontSize: '0.6rem' }} onClick={() => setEditing(false)}>Cancel</Button>
          </>
        ) : (
          <>
            <Chip size="small" label={`TP ${fmtSigned(fp.tp)}`} sx={{ height: 16, fontSize: '0.55rem', fontWeight: 800, bgcolor: C.bullMuted, color: C.bull }} />
            <Chip size="small" label={`SL ${fmtSigned(-fp.sl)}`} sx={{ height: 16, fontSize: '0.55rem', fontWeight: 800, bgcolor: C.bearMuted, color: C.bear }} />
            <Tooltip arrow title="Closes the whole basket when its TOTAL P&L — what adjustments already booked plus the open legs — reaches the target or the stop. Adjustments never move these; no trail touches them. The stop must persist briefly before it fires (noise filter)."><InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} /></Tooltip>
            <Button size="small" variant="text" sx={{ minWidth: 0, py: 0, px: 0.6, fontSize: '0.58rem' }} onClick={() => setEditing(true)}>Edit</Button>
            <Button size="small" variant="text" color="inherit" disabled={saving} sx={{ minWidth: 0, py: 0, px: 0.6, fontSize: '0.58rem', color: 'text.secondary' }}
              onClick={() => { if (window.confirm('Switch this basket to premium-% exits (re-based after each adjustment)?')) save({ exitMode: 'PREMIUM' }, `Basket #${b.id}: premium-% exits`); }}>Use premium %</Button>
          </>
        )}
      </Box>
    </Box>
  );
};

/** Total-P&L gauge for a fixed-₹ basket: stop on the left, target on the right. */
const FixedGauge: React.FC<{ tp: number; sl: number; total: number }> = ({ tp, sl, total }) => {
  const { C } = useAppTheme();
  // sl is signed: the stop sits at total −sl (a profit floor when sl < 0).
  const stopAt = -sl, span = (tp - stopAt) || 1;
  const zero = Math.max(0, Math.min(100, (-stopAt / span) * 100)), pos = Math.max(0, Math.min(100, ((total - stopAt) / span) * 100));
  return (
    <Box sx={{ mt: 0.5 }}>
      <Box sx={{ position: 'relative', height: 10, borderRadius: 5, background: `linear-gradient(90deg, ${stopAt >= 0 ? C.bullMuted : C.bearMuted}, transparent ${zero}%, ${C.bullMuted})`, border: '1px solid', borderColor: 'divider' }}>
        {stopAt < 0 && <Box sx={{ position: 'absolute', top: -3, bottom: -3, left: `${zero}%`, width: '1px', bgcolor: 'text.disabled' }} />}
        <Box sx={{ position: 'absolute', top: -4, width: 5, height: 16, borderRadius: 1, left: `calc(${pos}% - 2px)`, bgcolor: total >= 0 ? C.bull : C.bear }} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3 }}>
        <Typography variant="caption" sx={{ color: C.bear, fontWeight: 700 }}>SL {fmtSigned(-sl)}{sl < 0 ? ' (profit lock)' : ''}</Typography>
        <Typography variant="caption" color="text.secondary">total {fmtSigned(total)}</Typography>
        <Typography variant="caption" sx={{ color: C.bull, fontWeight: 700 }}>TP {fmtSigned(tp)}</Typography>
      </Box>
    </Box>
  );
};

/** Inline editor for an OPEN basket's auto-close levels: book at tpPct% of MAX PROFIT, stop at
 *  slPct% of MAX LOSS (the defined risk). Persists via PUT /baskets/:id/levels which re-baselines
 *  targetPremium/slPremium via the shared computeBasketTpSl. */
const TpSlEditor: React.FC<{ b: Basket; onRefresh: () => void; dense?: boolean }> = ({ b, onRefresh, dense }) => {
  const { C } = useAppTheme();
  const [editing, setEditing] = useState(false);
  const [tp, setTp] = useState<number>(b.tpPct != null ? Number(b.tpPct) : 50);
  const [sl, setSl] = useState<number>(b.slPct != null ? Number(b.slPct) : 50);
  const [saving, setSaving] = useState(false);
  // Re-sync from server when not editing (poll refreshes the basket every 15s).
  useEffect(() => { if (!editing) { setTp(b.tpPct != null ? Number(b.tpPct) : 50); setSl(b.slPct != null ? Number(b.slPct) : 50); } }, [b.tpPct, b.slPct, editing]);

  const save = async () => {
    setSaving(true);
    try {
      await optionsService.updateBasketLevels(b.id, { tpPct: tp, slPct: sl });
      toast.success(`Basket #${b.id}: auto-close set to TP ${tp}% / SL ${sl}%`, { duration: 5000 });
      setEditing(false);
      onRefresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.message || 'Update failed', { duration: 7000 });
    } finally { setSaving(false); }
  };

  const tpShown = b.tpPct != null ? Number(b.tpPct) : 50;
  const slShown = b.slPct != null ? Number(b.slPct) : 50;
  // Exit math — uses live (typed) % while editing, persisted % otherwise.
  const em = basketExitMath(b, editing ? tp : undefined, editing ? sl : undefined);
  const roi = (v: number | null) => (v != null ? ` · ${v >= 0 ? '+' : '−'}${Math.abs(Math.round(v))}% ROI` : '');

  /* Premium base + ₹ profit/loss at each exit route — so the % is never shown
     without the amount it's computed against or the resulting P&L. */
  const ExitMath: React.FC = () => em ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap', mt: 0.4 }}>
      <Typography sx={{ fontSize: dense ? '0.58rem' : '0.62rem', color: 'text.secondary' }}>
        At your exit levels{editing ? ' (preview)' : ''}:
      </Typography>
      <Typography sx={{ fontSize: dense ? '0.58rem' : '0.62rem', fontWeight: 800, color: C.bull }}>
        TP {fmtSigned(em.tpInr)}{roi(em.tpRoi)}
      </Typography>
      <Typography sx={{ fontSize: dense ? '0.58rem' : '0.62rem', fontWeight: 800, color: C.bear }}>
        SL {fmtSigned(-em.slInr)}{roi(em.slRoi != null ? -em.slRoi : null)}
      </Typography>
    </Box>
  ) : null;

  if (!editing) {
    return (
      <Box sx={{ mt: dense ? 0.4 : 0.6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: dense ? '0.6rem' : '0.64rem', color: 'text.secondary', fontWeight: 700 }}>Auto-close</Typography>
          <Chip size="small" label={`TP ${tpShown}%`} sx={{ height: 16, fontSize: '0.55rem', fontWeight: 800, bgcolor: C.bullMuted, color: C.bull }} />
          <Chip size="small" label={`SL ${slShown}%`} sx={{ height: 16, fontSize: '0.55rem', fontWeight: 800, bgcolor: C.bearMuted, color: C.bear }} />
          <Tooltip arrow title="TP books when profit reaches TP% of this basket's MAX PROFIT; SL closes when the loss reaches SL% of its MAX LOSS (the defined risk) — with a floor of 2× the credit for credit structures. ROI is on the estimated margin. A stop must also persist for a tick before it fires (noise filter)."><InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} /></Tooltip>
          <Button size="small" variant="text" sx={{ minWidth: 0, py: 0, px: 0.6, fontSize: '0.58rem' }} onClick={() => setEditing(true)}>Edit</Button>
        </Box>
        <ExitMath />
      </Box>
    );
  }
  return (
    <Box sx={{ mt: dense ? 0.4 : 0.6, p: 0.6, borderRadius: 1, bgcolor: 'action.hover' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'text.secondary' }}>Auto-close:</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
          <Typography sx={{ fontSize: '0.58rem', color: C.bull, fontWeight: 700 }}>TP</Typography>
          <InputBase value={tp}
            onChange={e => { const n = parseInt(e.target.value.replace(/[^0-9]/g, ''), 10); setTp(Number.isFinite(n) ? Math.max(1, Math.min(99, n)) : 50); }}
            onFocus={e => e.target.select()}
            inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', padding: 0, fontSize: '0.72rem', fontWeight: 800 } }}
            sx={{ width: 30, px: 0.3, border: '1px solid', borderColor: 'divider', borderRadius: 0.5 }} />
          <Typography sx={{ fontSize: '0.58rem', color: 'text.secondary' }}>%</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
          <Typography sx={{ fontSize: '0.58rem', color: C.bear, fontWeight: 700 }}>SL</Typography>
          <InputBase value={sl}
            onChange={e => { const n = parseInt(e.target.value.replace(/[^0-9]/g, ''), 10); setSl(Number.isFinite(n) ? Math.max(5, Math.min(500, n)) : 100); }}
            onFocus={e => e.target.select()}
            inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', padding: 0, fontSize: '0.72rem', fontWeight: 800 } }}
            sx={{ width: 36, px: 0.3, border: '1px solid', borderColor: 'divider', borderRadius: 0.5 }} />
          <Typography sx={{ fontSize: '0.58rem', color: 'text.secondary' }}>%</Typography>
        </Box>
        <Button size="small" variant="contained" sx={{ minWidth: 0, py: 0, px: 1, fontSize: '0.58rem' }} disabled={saving} onClick={save}>
          {saving ? '…' : 'Save'}
        </Button>
        <Button size="small" variant="text" sx={{ minWidth: 0, py: 0, px: 0.6, fontSize: '0.58rem' }} disabled={saving} onClick={() => setEditing(false)}>Cancel</Button>
      </Box>
      <ExitMath />
    </Box>
  );
};

const CompactRow: React.FC<{ b: Basket; onClose: () => void; onRefresh: () => void }> = ({ b, onClose, onRefresh }) => {
  const { C } = useAppTheme();
  const isPaper = b.tradingMode === 'PAPER';
  const live = b.live;
  const pnl = live?.unrealizedPnl;
  const dte = dteFmt(live?.nearExpiryMs);
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ borderRadius: 1, bgcolor: 'action.hover', border: b.adjustmentNeeded ? '1px solid' : 'none', borderColor: b.adjustmentInfo?.severity === 'urgent' ? C.bear : C.warn }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6, px: 1, flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <IconButton size="small" sx={{ p: 0 }}>{open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}</IconButton>
        <Chip size="small" label={`#${b.id}`} sx={{ height: 18, fontSize: '0.58rem', fontWeight: 800 }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>{b.strategy}</Typography>
        <Chip size="small" label={isPaper ? 'PAPER' : 'LIVE'} color={isPaper ? 'warning' : 'error'} sx={{ height: 16, fontSize: '0.52rem', fontWeight: 800 }} />
        {b.levelTriggerId != null && <Chip size="small" label="⚡ Level" color="warning" variant="outlined" data-level-origin={b.levelTriggerId} sx={{ height: 16, fontSize: '0.52rem', fontWeight: 800 }} />}
        {b.adjustmentNeeded && <Chip size="small" label="⚠ Adjust" color={b.adjustmentInfo?.severity === 'urgent' ? 'error' : 'warning'} sx={{ height: 16, fontSize: '0.52rem', fontWeight: 800 }} />}
        {dte && <Typography sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>· {dte.label} left</Typography>}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: pnl == null ? 'text.disabled' : pnl >= 0 ? C.bull : C.bear }}>
            {pnl == null ? 'live…' : fmtSigned(pnl)}
          </Typography>
          <Button size="small" variant="outlined" color="error" sx={{ minWidth: 0, py: 0, px: 1, fontSize: '0.6rem' }} onClick={e => { e.stopPropagation(); onClose(); }}>Close</Button>
        </Box>
      </Box>
      {open && (
        <Box sx={{ px: 0.5, pb: 0.5 }}>
          {(() => { const s = liveBasketStatus(b); return s ? (<Box sx={{ mb: 0.6 }}><PlainStatus tone={s.tone} emoji={s.emoji}>{s.text}</PlainStatus></Box>) : null; })()}
          <TpSlEditor b={b} onRefresh={onRefresh} dense />
          <BasketAdjustPanel basketId={b.id} strategy={b.strategy} tradingMode={b.tradingMode} contractValue={b.legFills?.[0]?.contractValue} onAdjusted={onRefresh} />
        </Box>
      )}
    </Box>
  );
};

const BasketRow: React.FC<{ b: Basket; onClose: () => void; onRefresh: () => void }> = ({ b, onClose, onRefresh }) => {
  const { C } = useAppTheme();
  const [expanded, setExpanded] = useState(false);
  const [showPayoff, setShowPayoff] = useState(false);
  const phase = phaseOf(b);
  const isPaper = b.tradingMode === 'PAPER';
  const pnl = b.realizedPnl != null ? Number(b.realizedPnl) : null;
  const live = b.live;
  type ChipColor = 'info' | 'success' | 'default';
  const statusChip: { label: string; color: ChipColor } = phase === 'open'
    ? { label: 'OPEN · monitoring', color: 'info' }
    : phase === 'closed'
      ? { label: `CLOSED · ${b.exitReason || 'done'}`, color: pnl != null && pnl >= 0 ? 'success' : 'default' }
      : phase === 'pending'
        ? { label: 'PENDING · awaiting fill', color: 'default' }
        : { label: b.status, color: 'default' };
  const lots = b.legs?.[0]?.quantity ?? 0;

  // Live per-leg mark by broker symbol; entry fill by symbol (legs descending by strike).
  const markBySym = new Map<string, number>();
  for (const l of (live?.perLeg || [])) markBySym.set(l.symbol, l.mark);
  const fillBySym = new Map<string, number>();
  for (const f of (b.legFills || [])) { if (f.symbol && f.fillPrice != null) fillBySym.set(f.symbol, Number(f.fillPrice)); }
  const sortedLegs = [...(b.legs || [])].sort((a, c) => c.strike - a.strike);

  const dte = dteFmt(live?.nearExpiryMs);
  // Profit zone (short strikes) vs spot.
  const shortPut = b.legs?.filter(l => l.action === 'SELL' && l.optionType === 'PE').map(l => l.strike).sort((a, c) => a - c)[0];
  const shortCall = b.legs?.filter(l => l.action === 'SELL' && l.optionType === 'CE').map(l => l.strike).sort((a, c) => c - a)[0];
  const spot = live?.spot ?? null;
  const inZone = spot != null && (shortPut == null || spot >= shortPut) && (shortCall == null || spot <= shortCall);

  const livePnl = live?.unrealizedPnl;
  const capturePct = livePnl != null && b.maxProfit ? (livePnl / Number(b.maxProfit)) * 100 : null;

  /* CLOSED baskets collapse to one line.
   *
   * The Trades page opened on 50 of these, every one CLOSED, each a ~340px card
   * with legs, entry premium, three metrics and an outcome explainer — roughly
   * 17,000px of history sitting above the actual trades table, and most of the
   * page's 9,978px. A closed basket is a record, not something you act on: it
   * gets a row you can read in one glance and expand when you want the detail.
   * Open and pending baskets are untouched. */
  const [cardOpen, setCardOpen] = useState(false);
  if (phase === 'closed' && !cardOpen) {
    const oc = outcomeOf(b);
    const tint = oc.tone === 'good' ? C.bull : oc.tone === 'bad' ? C.bear : C.warn;
    return (
      <Box
        onClick={() => setCardOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCardOpen(true); } }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          px: 1.25, py: 0.7, borderRadius: 1,
          border: '1px solid', borderColor: 'divider',
          borderLeft: `3px solid ${tint}`,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Chip size="small" label={`#${b.id}`} sx={{ height: 18, fontWeight: 800, fontSize: CH_TYPE.micro }} />
        <Typography noWrap sx={{ fontWeight: 700, fontSize: CH_TYPE.small, minWidth: 0 }}>{b.strategy}</Typography>
        <Chip size="small" label={isPaper ? 'PAPER' : 'LIVE'} color={isPaper ? 'warning' : 'error'}
          sx={{ height: 17, fontSize: CH_TYPE.micro, fontWeight: 800 }} />
        <Typography noWrap sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary', minWidth: 0 }}>
          {oc.emoji} {oc.title}
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: CH_TYPE.small, color: tint, ...chNumeric }}>
            {pnl != null ? fmtSigned(pnl) : '—'}
          </Typography>
          <Typography noWrap sx={{ fontSize: CH_TYPE.micro, color: 'text.disabled' }}>
            {b.closedAt ? ts(b.closedAt) : ''}
          </Typography>
          <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
        </Box>
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ borderLeft: `4px solid ${phase === 'open' ? C.neutral : phase === 'closed' ? (pnl != null && pnl >= 0 ? C.bull : C.dim) : C.dim}` }}>
      <CardContent sx={{ pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5, flexWrap: 'wrap' }}>
          <Chip size="small" label={`#${b.id}`} sx={{ height: 20, fontWeight: 800 }} />
          <Typography sx={{ fontWeight: 800 }}>{b.strategy}</Typography>
          <Chip size="small" label={isPaper ? 'PAPER' : 'LIVE'} color={isPaper ? 'warning' : 'error'} sx={{ height: 18, fontSize: '0.58rem', fontWeight: 800 }} />
          {/* Adopted from positions opened directly at the broker (item 16). */}
          {b.levelTriggerId != null && <Chip size="small" label="⚡ Level" color="warning" variant="outlined" data-level-origin={b.levelTriggerId}
            title="Opened automatically when the price level in its name fired (option strategy attached to an NB/NS/BB/BS level)." sx={{ height: 18, fontSize: '0.56rem', fontWeight: 800 }} />}
          {(b as any).source === 'BROKER' && <Chip size="small" label="Broker" color="info" title="Adopted from a position opened directly at the broker — set TP/SL to enable auto-monitoring." sx={{ height: 18, fontSize: '0.56rem', fontWeight: 800 }} />}
          {b.adjustmentNeeded && phase === 'open' && <Chip size="small" label="⚠ Adjust" color={b.adjustmentInfo?.severity === 'urgent' ? 'error' : 'warning'} sx={{ height: 18, fontSize: '0.56rem', fontWeight: 800 }} />}
          {b.desynced && phase === 'open' && <Chip size="small" label="⛓ Out of sync" color="error" sx={{ height: 18, fontSize: '0.56rem', fontWeight: 800 }} />}
          <Chip size="small" label={statusChip.label} color={statusChip.color} sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, ml: 'auto', textTransform: 'capitalize' }} />
        </Box>

        {/* DESYNCED — a leg was closed/reduced OUTSIDE the app; auto TP/SL is frozen.
            Resolve by adopting broker truth (rewrite the basket to what actually
            remains) or by closing whatever is really there. */}
        {b.desynced && phase === 'open' && (
          <Box sx={{ mt: 0.5, mb: 0.5, p: 1, borderRadius: 1, border: `1px solid ${C.bear}`, bgcolor: C.bearMuted }}>
            <Typography variant="caption" sx={{ display: 'block', color: C.bear, fontWeight: 700 }}>
              Broker positions no longer match this basket — automatic TP/SL is FROZEN (no blind exits).
            </Typography>
            {(b.desyncInfo?.legs || []).map((x, i) => (
              <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', color: 'text.secondary' }}>
                {x.symbol}: app expects net {x.expected}, broker shows {x.broker}
              </Typography>
            ))}
            <Button size="small" variant="outlined" color="error" sx={{ mt: 0.5, fontSize: '0.62rem', py: 0 }}
              onClick={async () => {
                try {
                  const r: any = await optionsService.adoptBroker(b.id);
                  const d = r?.data?.data;
                  toast.success(d?.closed
                    ? `Basket #${b.id}: broker is flat — book-closed. Check the broker statement for the realised P&L.`
                    : `Basket #${b.id}: adopted broker truth — kept ${d?.keptLegs} leg(s), dropped ${d?.droppedLegs}; TP/SL re-baselined and monitoring resumed.`,
                    { duration: 8000 });
                } catch (e: any) {
                  toast.error(e?.response?.data?.error?.message || 'Adopt failed', { duration: 8000 });
                }
              }}>
              Adopt broker truth
            </Button>
          </Box>
        )}

        {/* Legs: entry fill → live mark (when open). */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
          {sortedLegs.map(l => {
            const fp = fillBySym.get(l.symbol); const mk = markBySym.get(l.symbol);
            return `${l.action === 'BUY' ? '+' : '−'}${l.optionType}${l.strike}${fp != null ? `@${fp}` : ''}${mk != null ? `→${mk}` : ''}`;
          }).join(' / ')} · ×{lots} {lots === 1 ? 'lot' : 'lots'}
        </Typography>
        {b.entryCredit != null && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
            Entry net premium: {Number(b.entryCredit) >= 0 ? '+' : '−'}{Math.abs(Number(b.entryCredit)).toFixed(2)} {Number(b.entryCredit) >= 0 ? '(credit)' : '(debit)'}
            {(() => { const v = premiumInr(b, Math.abs(Number(b.entryCredit))); return v != null ? <> ≈ {fmtInr(v)}</> : null; })()}
            {live != null && <> · now {live.currentNet.toFixed(2)}</>}
          </Typography>
        )}

        {/* OPEN: live unrealised P&L is the headline. CLOSED: realised. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mt: 1 }}>
          {phase === 'open'
            ? (fixedPlan(b) || adjBooked(b)
              ? <Metric label="P&L (total, net)" value={livePnl != null ? fmtSigned(livePnl + adjBooked(b)) : 'marking…'} color={livePnl != null ? (livePnl + adjBooked(b) >= 0 ? C.bull : C.bear) : undefined}
                  sub={`open ${livePnl != null ? fmtSigned(livePnl) : '—'} · adjustments ${fmtSigned(adjBooked(b))}${Number(b.live?.costInr) > 0 ? ` · after ≈${fmtInr(Number(b.live!.costInr))} costs` : ''}`} />
              : <Metric label="P&L (live, net)" value={livePnl != null ? fmtSigned(livePnl) : 'marking…'} color={livePnl != null ? (livePnl >= 0 ? C.bull : C.bear) : undefined}
                  sub={[Number(b.live?.costInr) > 0 ? `after ≈${fmtInr(Number(b.live!.costInr))} costs` : '', capturePct != null ? `${Math.round(capturePct)}% of max` : ''].filter(Boolean).join(' · ') || undefined} />)
            : <Metric label="P&L (realised)" value={pnl != null ? fmtSigned(pnl) : '—'} color={pnl != null ? (pnl >= 0 ? C.bull : C.bear) : undefined}
                sub={(() => {
                  if (pnl == null) return undefined;
                  if (pnl >= 0 && b.maxProfit) return `${Math.round((pnl / Number(b.maxProfit)) * 100)}% of max profit`;
                  if (pnl < 0 && b.maxLoss) return `${Math.round((Math.abs(pnl) / Math.abs(Number(b.maxLoss))) * 100)}% of max loss`;
                  return undefined;
                })()} />}
          <Metric label={<Term k="maxProfit">Max profit</Term>} value={b.maxProfit != null ? fmtInr(Number(b.maxProfit)) : '—'} color={C.bull} />
          {fixedPlan(b) && phase === 'open'
            ? <Metric label="Stop (fixed)" value={fmtSigned(-fixedPlan(b)!.sl)} color={C.bear} sub={`structure max loss ${b.maxLoss != null ? fmtInr(Math.abs(Number(b.maxLoss))) : '—'}`} />
            : <Metric label={<Term k="maxLoss">Max loss</Term>} value={b.maxLoss != null ? fmtInr(Math.abs(Number(b.maxLoss))) : '—'} color={C.bear} />}
        </Box>
        {(b.paperFill === 'BOOK' || (b.adjustmentLog || []).length > 0) && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.4 }}>
            {b.paperFill === 'BOOK' ? 'Paper fills: live order book (slippage included) · valued at bid/ask. ' : ''}
            {(b.adjustmentLog || []).length > 0 ? `${(b.adjustmentLog || []).length} adjustment${(b.adjustmentLog || []).length > 1 ? 's' : ''} · booked ${fmtSigned(adjBooked(b))}` : ''}
          </Typography>
        )}

        {/* CLOSED: plain-English outcome — WHY it closed + win/loss + key stats. */}
        {phase === 'closed' && (() => {
          const oc = outcomeOf(b);
          const tint = oc.tone === 'good' ? C.bull : oc.tone === 'bad' ? C.bear : C.warn;
          const held = heldFor(b.createdAt, b.closedAt);
          const rom = pnl != null && b.marginEstimate ? (pnl / Number(b.marginEstimate)) * 100 : null;
          return (
            <Box sx={{ mt: 1, p: 1, borderRadius: 1, borderLeft: `3px solid ${tint}`, bgcolor: (t) => t.palette.mode === 'dark' ? `${tint}1A` : `${tint}0D` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: tint }}>
                  {oc.emoji} {oc.title}
                </Typography>
                <Chip size="small" label={pnl == null ? 'closed' : pnl >= 0 ? 'PROFIT' : 'LOSS'}
                  sx={{ height: 16, fontSize: '0.52rem', fontWeight: 800, bgcolor: tint, color: '#fff' }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3, lineHeight: 1.35 }}>
                {oc.detail}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
                {b.entryCredit != null && (
                  <Typography variant="caption" color="text.disabled">
                    Entry {Number(b.entryCredit) >= 0 ? '+' : '−'}{Math.abs(Number(b.entryCredit)).toFixed(2)} {Number(b.entryCredit) >= 0 ? 'credit' : 'debit'}
                    {(() => { const v = premiumInr(b, Math.abs(Number(b.entryCredit))); return v != null ? ` ≈ ${fmtInr(v)}` : ''; })()}
                  </Typography>
                )}
                {held && <Typography variant="caption" color="text.disabled">⏱ held {held}</Typography>}
                {rom != null && <Typography variant="caption" color="text.disabled">{rom >= 0 ? '+' : ''}{rom.toFixed(1)}% on margin</Typography>}
                <Button size="small" variant={showPayoff ? 'contained' : 'outlined'} sx={{ ml: 'auto', py: 0, fontSize: '0.62rem' }}
                  startIcon={<ShowChartIcon fontSize="small" />} onClick={() => setShowPayoff(s => !s)}>
                  {showPayoff ? 'Hide payoff' : 'What happened? · View payoff'}
                </Button>
              </Box>
              {showPayoff && (
                <ClosedBasketPayoff strategy={b.strategy} legs={b.legs as any} legFills={b.legFills as any}
                  entrySpot={b.entrySpot} entryCredit={b.entryCredit} maxProfit={b.maxProfit} maxLoss={b.maxLoss} breakEvens={b.breakEvens} />
              )}
            </Box>
          );
        })()}

        {/* PENDING (placed, no legs executed yet) — payoff is still viewable from the legs. */}
        {phase === 'pending' && (
          <Box sx={{ mt: 1, p: 1, borderRadius: 1, borderLeft: `3px solid ${C.dim}`, bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,.04)' : '#f6f7f9' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">Not filled yet — armed{b.campaignId ? ` (staged campaign #${b.campaignId})` : ''}. Here's the payoff it would carry when it fills:</Typography>
              <Button size="small" variant="outlined" color="error" sx={{ ml: 'auto', py: 0, fontSize: '0.62rem' }}
                onClick={async () => { if (!window.confirm(`Remove pending basket #${b.id}? It won't be placed.`)) return; try { await optionsService.rejectBasket(b.id); toast.success(`Pending basket #${b.id} removed`); onRefresh(); } catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Remove failed'); } }}>
                Remove
              </Button>
              <Button size="small" variant={showPayoff ? 'contained' : 'outlined'} sx={{ py: 0, fontSize: '0.62rem' }}
                startIcon={<ShowChartIcon fontSize="small" />} onClick={() => setShowPayoff(s => !s)}>
                {showPayoff ? 'Hide payoff' : 'View payoff'}
              </Button>
            </Box>
            {showPayoff && (
              <ClosedBasketPayoff strategy={b.strategy} legs={b.legs as any} legFills={b.legFills as any}
                entrySpot={b.entrySpot} entryCredit={b.entryCredit} maxProfit={b.maxProfit} maxLoss={b.maxLoss} breakEvens={b.breakEvens} />
            )}
          </Box>
        )}

        {/* Plain-English "how's it doing" — the one line a beginner reads. */}
        {phase === 'open' && (() => { const s = liveBasketStatus(b); return s ? (
          <Box sx={{ mt: 1 }}><PlainStatus tone={s.tone} emoji={s.emoji}>{s.text}</PlainStatus></Box>
        ) : null; })()}

        {phase === 'open' && (
          <>
            <Divider sx={{ my: 1 }} />
            {/* Campaign tranche → its exits are governed by the CAMPAIGN (₹ SL/TP), not this
                tranche's own premium levels. Don't show the (misleading, non-binding) tranche
                TP/SL gauge; point to the campaign instead. */}
            {b.campaignId ? (
              <Box sx={{ p: 1, mb: 0.5, borderRadius: 1.5, bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(25,118,210,.12)' : 'rgba(25,118,210,.08)', border: t => `1px solid ${t.palette.divider}` }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>🧩 Tranche of staged campaign #{b.campaignId}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Exits are governed by the CAMPAIGN's ₹ stop / take-profit (which track the whole position), not this tranche's own levels. Manage it on <strong>Options → Monitor</strong> or from the on-chart campaign badge.
                </Typography>
              </Box>
            ) : (
              /* TP/SL gauge (standalone basket) */
              fixedPlan(b)
                ? (live != null && <FixedGauge tp={fixedPlan(b)!.tp} sl={fixedPlan(b)!.sl} total={live.unrealizedPnl + adjBooked(b)} />)
                : live != null && b.entryCredit != null && b.targetPremium != null && b.slPremium != null && (
                  <TpSlGauge entry={Number(b.entryCredit)} target={Number(b.targetPremium)} sl={Number(b.slPremium)} now={live.currentNet} />
                )
            )}
            {/* Expiry + spot-vs-zone */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.8 }}>
              {dte && <Typography variant="caption" color="text.secondary">⏱ <strong>{dte.label}</strong> to near expiry</Typography>}
              {(shortPut != null || shortCall != null) && (
                <Typography variant="caption" sx={{ color: inZone ? C.bull : C.bear }}>
                  {spot != null ? `Spot ${Math.round(spot).toLocaleString('en-IN')}` : 'Spot —'} · zone {shortPut != null ? Math.round(shortPut).toLocaleString('en-IN') : '—'}–{shortCall != null ? Math.round(shortCall).toLocaleString('en-IN') : '—'} {spot != null ? (inZone ? '✓ in zone' : '⚠ outside') : ''}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 180 }}>
                {b.campaignId
                  ? <>Campaign-managed — the staged campaign governs the exit; this tranche is squared off with the rest.</>
                  : fixedPlan(b)
                    ? <>Auto-managed — closes at a <b>net</b> total P&amp;L of <strong>{fmtSigned(fixedPlan(b)!.tp)}</strong> or <strong>{fmtSigned(-fixedPlan(b)!.sl)}</strong> (adjustments + open legs, after closing at the bid / ask and the fees). The stop never moves on its own.{Number(b.ratioAlertX) > 0 ? <> Premium-ratio alert at {Number(b.ratioAlertX)}×.</> : null} Telegram alert on close.</>
                    : <>Auto-managed — TP at <strong>{b.targetPremium != null ? Number(b.targetPremium).toFixed(2) : '—'}</strong> / SL at <strong>{b.slPremium != null ? Number(b.slPremium).toFixed(2) : '—'}</strong> (net-to-close after slippage and fees). The stop never moves on its own. Telegram alert on close.</>}
              </Typography>
              <Button size="small" variant={expanded ? 'contained' : 'outlined'} color={b.adjustmentNeeded ? 'warning' : 'primary'}
                startIcon={expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                onClick={() => setExpanded(e => !e)}>
                Legs &amp; adjust
              </Button>
              <Button size="small" variant="outlined" color="error" onClick={onClose}>Close now</Button>
            </Box>
            {!b.campaignId && (fixedPlan(b) ? <FixedExitEditor b={b} onRefresh={onRefresh} /> : <TpSlEditor b={b} onRefresh={onRefresh} />)}
            {expanded && <Box sx={{ mt: 1 }}><BasketAdjustPanel basketId={b.id} strategy={b.strategy} tradingMode={b.tradingMode} contractValue={b.legFills?.[0]?.contractValue} onAdjusted={onRefresh} /></Box>}
          </>
        )}
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
          Placed {ts(b.createdAt)}{b.closedAt ? ` · closed ${ts(b.closedAt)}` : ''}
        </Typography>
      </CardContent>
    </Card>
  );
};

const CloseDialog: React.FC<{ target: Basket | null; closing: boolean; onCancel: () => void; onConfirm: () => void }> = ({ target, closing, onCancel, onConfirm }) => {
  const { C } = useAppTheme();
  return (
  <Dialog open={!!target} onClose={() => { if (!closing) onCancel(); }} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>Close basket #{target?.id}?</DialogTitle>
    <DialogContent>
      <Typography variant="body2">
        This squares off <strong>{target?.strategy}</strong> {target?.tradingMode === 'PAPER' ? '(PAPER — simulated)' : 'with REAL reduce-only market orders on every leg'} immediately. The realised P&L will be booked and the monitor stops managing it.
      </Typography>
      {target?.live != null && (
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 700, color: target.live.unrealizedPnl >= 0 ? C.bull : C.bear }}>
          Current live P&L ≈ {fmtSigned(target.live.unrealizedPnl)}
        </Typography>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onCancel} disabled={closing}>Cancel</Button>
      <Button variant="contained" color="error" onClick={onConfirm} disabled={closing} startIcon={closing ? <CircularProgress size={15} color="inherit" /> : undefined}>
        {closing ? 'Closing…' : 'Close now'}
      </Button>
    </DialogActions>
  </Dialog>
  );
};

const Metric: React.FC<{ label: React.ReactNode; value: string; color?: string; sub?: string }> = ({ label, value, color, sub }) => (
  <Box>
    <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, letterSpacing: 0.3, color: 'text.secondary', textTransform: 'uppercase' }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: color || 'text.primary' }}>{value}</Typography>
    {sub && <Typography sx={{ fontSize: '0.54rem', color: 'text.secondary' }}>{sub}</Typography>}
  </Box>
);

export default OptionBasketsMonitor;
